import BaseDataProvider from '../../core/BaseDataProvider';
import _ from 'lodash';
import { YTChannel } from '../../models/ChannelModel';
import WhiteLabelSiteModel from '../../models/WhiteLabelSiteModel';
import { GoogleAdsCIDModel, GoogleAdsCampaignModel, GoogleAdsAdGroupModel, UserReferralTokenModel } from '../../models/GoogleAdsModels';

class AuthDataProvider extends BaseDataProvider {

	//Login/Registration

	login(username, password) {
		return this.postLogin('/api/v1/auth/', {username: username, password: password}, false);
	}

	sendAuthMethod(authToken, method) {
		return this.postLogin('/api/v1/auth/', {auth_token: authToken, mfa_type: method}, false);
	}

	sendVerificationCode(authToken, session, code) {
		return this.postLogin('/api/v1/auth/', {auth_token: authToken, session: session, answer: code}, false)
	}

	loginWithGoogle(token) {
		return this.postLogin('/api/v1/auth/', {token: token}, false);
	}

	autoLogin(tokenObject) {
		return this.postLogin('/api/v1/auth/', tokenObject, false);
	}

	loginWithReferralToken(referralToken, referralTokenType = 'channelfactory_iq') {
		return this.postLogin('/api/v1/auth/', { referral_token: referralToken, referral_token_type: referralTokenType }, false);
	}

	requestMagicLinkEmail(email) {
		let data = {username: email, mfa_type: 'magic_link'};
		return this.post('api/v1/auth/', data, false);
	}

	register(data) {
		return this.post('/api/v1/users/', data, false);
	}

	updateCurrentUser(data) {
		return this.put('/api/v1/users/me/', data);
	}

	updateUserAvatar(img) {
		return this.imageUpload('/api/v1/users/me/avatar/', img);
	}

	deleteUserAvatar() {
		return this.delete('/api/v1/users/me/avatar/');
	}

	resetPassword(email) {
		return this.post('/api/v1/password_reset/', {email}, false);
	}

	changePassword(email, token, password) {
		return this.post('/api/v1/set_password/', {new_password: password, email, token}, false);
	}

	loginAsUser(userID) {
		return this.get(`/api/v1/admin/users/${userID}/auth/`);
	}

	changeUserPassword(currentPassword, newPassword) {
		return this.post('/api/v1/change_password/', {old_password: currentPassword, new_password: newPassword});
	}

	userLogout() {
		return this.delete('/api/v1/auth/');
	}

	getUserRegions() {
		return this.get('/api/v1/user_regions/');
	}

	getRoleRegions() {
		return this.get('/api/v1/role_regions/');
	}

	//Authorized Channels

	getAuthenticatedChannels() {
		return this.get('/api/v1/channels/?flat=1&own_channels=1').then(data => _.map(data.items, YTChannel.fromJSON.bind(YTChannel)));
	}

	sendChannelAuthentication(response) {
		return this.post('/api/v1/channels/authentication/', response);
	}

	revokeChannelAuth(channel) {
		return this.delete(`/api/v1/channels/${channel.id}/`);
	}

	//Contact Us Modal

	submitContactForm(form) {
		const { firstName, lastName, email, company, country, message } = form;
		const data = {first_name: firstName, last_name: lastName, email, company, country, message};
		return this.post('/api/v1/contact_forms/', data);
	}

	onGDPRCookiesAction(response) {
		if (response) {
			return this.put('/api/v1/users/me/', { has_accepted_GDPR: true });
		}
		return this.put('/api/v1/users/me/', { has_accepted_GDPR: false });
	}

	//OAuth
	fetchOAuthUserData() {
		return this.get('/api/v1/oauth/oauth_accounts/');
	}

	getGoogleAdsAccounts(code) {
		const href = encodeURIComponent(`${window.location.origin}${window.location.pathname.replace(/\/?(\?|#|$)/, '/$1')}?oauth_code=aw_auth`);
		return this.post(`/api/v1/oauth/aw_auth/?redirect_url=${href}`, code);
	}

	getDV360Accounts(code) {
		const href = encodeURIComponent(`${window.location.origin}${window.location.pathname.replace(/\/?(\?|#|$)/, '/$1')}?oauth_code=dv360_auth`);
		return this.post(`/api/v1/oauth/dv360_auth/?redirect_url=${href}`, code);
	}

	getMetaAccounts(code) {
		return this.post('/api/v1/oauth/meta_auth/', { code });
	}

	unlinkAccount(accountID) {
		return this.patch(`/api/v1/oauth/oauth_accounts/${accountID}/`, {is_enabled: false});
	}

	fetchCampaignsBasedOnOAuthType(oauthType, parentId) {
		let uri = `/api/v1/oauth/${oauthType?'line_items':'campaigns'}/?oauth_type=${oauthType}`;
		if (typeof parentId === 'number') uri += `&parent_id=${parentId}`;
		return this.get(uri);
	}

	fetchOAuthAdGroups(oauthType, parentId) {
		return this.get(`/api/v1/oauth/adgroups/?oauth_type=${oauthType}&parent_id=${parentId}`);
	}

	// White label site assets

	getSiteAssets() {
		return this.get('/api/v1/config/').then(data => WhiteLabelSiteModel.fromJSON(data));
	}

	// GoogleAds Push V2 // Meta
	fetchGAdsCID(search=null, page=1) {
		let uri = `/api/v1/oauth/gads_accounts/?page=${page}`;
		if (!_.isEmpty(search)) uri += `&search=${encodeURIComponent(search)}`;
		return this.get(uri).then(data => ({
			items: this.mapResponseItemsToObject(data.items, GoogleAdsCIDModel),
			currentPage: data.current_page,
			maxPage: data.max_page
		}));
	}

	fetchMetaAccounts(search=null, page=1) {
		let uri = `/api/v1/oauth/meta_accounts/?page=${page}`;
		if (!_.isEmpty(search)) uri += `&search=${encodeURIComponent(search)}`;
		return this.get(uri).then(data => ({
			items: this.mapResponseItemsToObject(data.items, GoogleAdsCIDModel),
			currentPage: data.current_page,
			maxPage: data.max_page
		}));
	}

	fetchPlatformCampaigns(accountID, typeID) {
		return this.get(`/api/v1/oauth/campaigns/?oauth_type=${typeID}&parent_id=${accountID}`)
			.then(data => this.mapResponseItemsToObject(data, GoogleAdsCampaignModel));
	}

	fetchPlatformAdGroupsByCampaign(campaignID, typeID) {
		return this.get(`/api/v1/oauth/adgroups/?oauth_type=${typeID}&campaign_id=${campaignID}`)
			.then(data => this.mapResponseItemsToObject(data, GoogleAdsAdGroupModel));
	}

	fetchPlatformAdGroupsByAccount(accountID, typeID) {
		return this.get(`/api/v1/oauth/adgroups/?oauth_type=${typeID}&account_id=${accountID}`)
			.then(data => this.mapResponseItemsToObject(data, GoogleAdsAdGroupModel));
	}

	fetchDV360InsertionOrders() {
		return this.get('/api/v1/oauth/insertion_orders/');
	}

	fetchDV360LineItems(ioId, page=1) {
		return this.get(`/api/v1/oauth/line_items/?page=${page}&parent_id=${ioId}`);
	}

	pushPlacementsToServerV2(segmentId, adgroups, exclusionId=null) {
		const body = {
			adgroup_ids: adgroups,
			segment_id: segmentId
		};
		if (_.isNumber(exclusionId)) body['exclusion_list_id'] = exclusionId;
		return this.post('/api/v2/segments/sync/gads/', body);
	}

	fetchReferralToken(tokenType) {
		return this.get(`/api/v1/referral_token/?token_type=${tokenType}`)
			.then(data => UserReferralTokenModel.fromJSON(data));
	}
}

const provider = new AuthDataProvider();
export default provider;
